import React from 'react';
import { Radio, RefreshCw } from 'lucide-react';
import { fmtTime, hrs } from './fabDashUtils';

const STATE = {
  working: { dot: 'bg-emerald-500 ring-2 ring-emerald-200', label: 'Working' },
  idle: { dot: 'bg-amber-400 ring-2 ring-amber-100', label: 'Clocked in, no job' },
  on_site: { dot: 'bg-sky-500 ring-2 ring-sky-200', label: 'On site' },
  done: { dot: 'bg-slate-300', label: 'Clocked out' },
};

const siteLine = (p) => {
  if (p.site_source === 'schedule') return 'Scheduled on site (dispatch)';
  if (p.clock_out_time) return `Site · out ${fmtTime(p.clock_out_time)}`;
  return `On site since ${fmtTime(p.clock_in_time)}${p.clocked_in_by ? ` · by ${p.clocked_in_by}` : ''}`;
};

export const FloorNow = ({ floor, onRefresh, refreshing }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="fab-floor-now">
    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        <Radio className="w-4 h-4 text-emerald-600" /> Floor right now
        {floor.crew_working > 0 && <span className="text-[10px] font-semibold bg-emerald-500 text-white rounded-full px-2 py-0.5">{floor.crew_working} live</span>}
      </h3>
      <button onClick={onRefresh} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="fab-refresh-btn">
        <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
      </button>
    </div>
    {floor.people.length === 0 ? (
      <p className="px-4 py-6 text-sm text-slate-500 text-center">Nobody has clocked in yet today</p>
    ) : (
      <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {floor.people.map((p) => (
          <li key={p.employee_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`floor-person-${p.employee_id}`}>
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATE[p.state].dot}`} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">
                {p.employee_name}
                {p.is_late && <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">late {p.late_minutes}m</span>}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {p.state === 'working' ? (<><span className="font-mono text-blue-700 font-semibold">{p.work_order_number}</span> {p.customer} · {hrs(p.on_job_hours)} on it</>)
                  : p.site_source ? (<><span className="inline-block text-[9px] font-bold text-sky-700 bg-sky-100 border border-sky-200 rounded px-1 mr-1 align-middle">ON SITE</span>{p.work_order_number && <span className="font-mono text-blue-700 font-semibold">{p.work_order_number} </span>}{p.customer} · {siteLine(p)}</>)
                  : p.state === 'idle' ? STATE.idle.label : `Out ${fmtTime(p.clock_out_time)}`}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-slate-900">{p.site_source ? (p.on_job_hours > 0 ? hrs(p.on_job_hours) : '') : hrs(p.hours_today)}</p>
              <p className="text-[10px] text-slate-500">{p.clock_in_time ? `in ${fmtTime(p.clock_in_time)}` : ''}</p>
            </div>
          </li>
        ))}
      </ul>
    )}
    {floor.active_wos.length > 0 && (
      <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50 rounded-b-xl">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">Jobs touched today</p>
        <div className="flex flex-wrap gap-1.5">
          {floor.active_wos.map((w) => (
            <span key={w.work_order_number} className={`text-xs rounded-md px-2 py-1 border ${w.active_count > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-slate-200 text-slate-700'}`}>
              <span className="font-mono font-semibold">{w.work_order_number}</span> · {hrs(w.total_hours)}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);
